import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./EditHotel.module.css";
import { useState, useEffect } from "react";
import { supabase } from "../supabase/client";
import Loader from "../components/ui/elements/custom/Loader";
import { IoChevronForwardSharp } from "react-icons/io5";

function EditHotel() {
  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [locality, setLocality] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [provider, setProvider] = useState("");
  const [apiId, setApiId] = useState("");
  const [active, setActive] = useState(false);
  const [originalData, setOriginalData] = useState();
  const [logo, setLogo] = useState(null);

  const [propertyTypes, setPropertyTypes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [localities, setLocalities] = useState([]);

  const navigate = useNavigate();
  const params = useParams();

  const fetchHotel = async (id) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("hotels")
        .select(
          "name, code, property_type_id, api_provider_id, api_identifier, active, logo_url, locality_id"
        )
        .eq("id", id)
        .single();

      if (error) {
        toast.error(
          () => (
            <>
              <p>Failed to retrieve hotel information!</p>
              <p className={styles.info}>Reason: {error.message}</p>
            </>
          ),
          {
            toastId: "errortoast",
          }
        );
        navigate("/hotels");
        return;
      }

      setOriginalData({
        name: data.name,
        code: data.code,
        property_type: data.property_type_id,
        api_provider: data.api_provider_id,
        api_identifier: data.api_identifier,
        active: data.active,
        logo_url: data.logo_url,
        locality: data.locality_id,
      });

      setName(data.name);
      setCode(data.code);
      setPropertyType(data.property_type_id);
      setProvider(data.api_provider_id);
      setApiId(data.api_identifier);
      setActive(data.active);
      setLogoUrl(data.logo_url);
      setLocality(data.locality_id);
    } catch (error) {
      toast.error(
        () => (
          <>
            <p>Failed to retrieve hotel information!</p>
            <p className={styles.info}>Reason: {error.message}</p>
          </>
        ),
        {
          toastId: "errortoast",
        }
      );
      navigate("/hotels");
    }

    setIsLoading(false);
  };

  const fetchProviders = async () => {
    try {
      const { data, error } = await supabase
        .from("api_service_providers")
        .select();

      if (error) {
        throw error;
      }

      setProviders(data);
    } catch (error) {
      toast.error("Failed to fetch API providers: " + error);
      return null;
    }
  };

  const fetchPropertyTypes = async () => {
    try {
      const { data, error } = await supabase.from("property_types").select();

      if (error) {
        throw error;
      }

      setPropertyTypes(data);
    } catch (error) {
      toast.error("Failed to fetch property types: " + error);
      return null;
    }
  };

  const fetchLocalities = async () => {
    try {
      const { data, error } = await supabase
        .from("localities")
        .select("id, created_at, name, countries(name)");

      if (error) {
        throw error;
      }

      setLocalities(data);
    } catch (error) {
      toast.error("Failed to fetch localities: " + error);
      return null;
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];

    // Check if a file was selected
    if (!file) {
      setLogo(null);
      return;
    }

    // Check file type
    const fileType = file.type.split("/")[1];

    const validFileTypes = ["png", "webp", "svg"];

    if (!validFileTypes.includes(fileType)) {
      toast.error("Invalid file type. Please upload a PNG, WEBP or SVG image.");

      return;
    }

    // Check file size (250kb limit)
    const fileSize = file.size / 1024 / 1024; // Convert to MB
    if (fileSize > 0.25) {
      toast.error("File size exceeds 250KB limit.");
      return;
    }

    // If valid, set the logo state
    setLogo(file);
  };

  const updateHotel = async (e, id) => {
    e.preventDefault();

    if (!name || !code || !provider || !locality || !apiId) {
      alert(
        "Hotel name, code, service provider, locality, and API ID are required."
      );
      return;
    }

    if (
      name === originalData.name &&
      code === originalData.code &&
      propertyType === originalData.property_type &&
      provider === originalData.api_provider &&
      locality === originalData.locality &&
      apiId === originalData.api_identifier &&
      active === originalData.active &&
      logo === null
    ) {
      toast.info("Nothing was changed!");

      return;
    }

    let newLogoUrl = null;

    // Upload the logo to Supabase storage if it exists
    if (logo) {
      // Remove existing logo if it exists
      if (logoUrl) {
        const path = logoUrl.split("/hotel-logos/")[1]?.replace(/^\/+/, ""); // removes any leading slashes

        if (path) {
          const { error: storageError } = await supabase.storage
            .from("hotel-logos")
            .remove([decodeURIComponent(path)]);

          if (storageError) {
            toast.warn(
              "Failed to delete logo from storage:",
              storageError.message
            );
          }
        }
      }

      // Build a filename
      const extension = logo.name.split(".").pop();
      const baseName = name
        .normalize("NFD") // decompose accents
        .replace(/[\u0300-\u036f]/g, "") // remove accents
        .replace(/\s+/g, "-") // replace spaces with hyphens
        .replace(/[^a-zA-Z0-9-_.]/g, "") // remove all non-safe characters
        .toLowerCase();
      const timestamp = Date.now().toString();
      const fileName = `${baseName}_${timestamp}.${extension}`;

      const { data: logoData, error: logoError } = await supabase.storage
        .from("hotel-logos")
        .upload(fileName, logo, {
          cacheControl: "3600",
          upsert: true,
        });

      if (logoError) {
        toast.error("Failed to upload logo: " + logoError.message);
        return;
      }

      const { data: urlData, error: urlError } = supabase.storage
        .from("hotel-logos")
        .getPublicUrl(logoData.path);

      if (urlError) {
        toast.error("Failed to get logo URL: " + urlError.message);
        return;
      }

      newLogoUrl = urlData.publicUrl;
    } else {
      newLogoUrl = logoUrl;
    }

    toast.promise(
      supabase
        .from("hotels")
        .update({
          name: name.trim(),
          code: code.trim(),
          property_type_id: propertyType,
          api_provider_id: provider,
          locality_id: locality,
          api_identifier: apiId.trim(),
          active: active,
          logo_url: newLogoUrl,
        })
        .eq("id", id)
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return data;
        }),
      {
        pending: `Updating ${name}...`,
        success: {
          render({ data }) {
            navigate("/hotels");
            return `${name} successfully updated!`;
          },
        },
        error: {
          render({ data }) {
            return (
              <>
                <p>Updating {name} failed!</p>
                <p className={styles.info}>Reason: {data.message}</p>
              </>
            );
          },
        },
      }
    );
  };

  useEffect(() => {
    if (params.id) {
      fetchHotel(params.id);
      fetchPropertyTypes();
      fetchProviders();
      fetchLocalities();
    }
  }, [params.id, navigate]);

  return (
    <>
      <div className={styles.main_container}>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <h4 className={styles.page_title}>
              Hotels <IoChevronForwardSharp /> Edit <IoChevronForwardSharp />{" "}
              {name}
            </h4>

            <form
              onSubmit={(e) => {
                updateHotel(e, params.id);
              }}
            >
              <div className={styles.form_container}>
                <label htmlFor="name" className={styles.labels}>
                  Name
                </label>
                <input
                  required
                  type="text"
                  placeholder="Hotel name"
                  id="name"
                  name="name"
                  className={styles.textboxes}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <label htmlFor="code" className={styles.labels}>
                  Code
                </label>
                <input
                  required
                  type="text"
                  placeholder="Code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  id="code"
                  name="code"
                  className={styles.textboxes}
                />

                <label htmlFor="property_type" className={styles.labels}>
                  Property type
                </label>
                <select
                  required
                  className={styles.textboxes}
                  name="property_type"
                  id="property_type"
                  value={propertyType}
                  onChange={(e) => setPropertyType(e.target.value)}
                >
                  <option value="">Select property type</option>
                  {propertyTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <label htmlFor="locality" className={styles.labels}>
                  Locality
                </label>
                <select
                  required
                  className={styles.textboxes}
                  name="locality"
                  id="locality"
                  value={locality}
                  onChange={(e) => setLocality(e.target.value)}
                >
                  <option value="">Select locality</option>
                  {localities.map((l) => (
                    <option key={l.id} value={l.id}>
                      {`${l.name}, ${l.countries?.name}`}
                    </option>
                  ))}
                </select>

                <label htmlFor="provider" className={styles.labels}>
                  Service Provider
                </label>
                <select
                  required
                  className={styles.textboxes}
                  name="provider"
                  id="provider"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                >
                  <option value="">Select service provider</option>
                  {providers.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>

                <label htmlFor="api_identifier" className={styles.labels}>
                  Resort Code
                </label>
                <input
                  required
                  type="number"
                  placeholder={10}
                  value={apiId}
                  onChange={(e) => setApiId(e.target.value)}
                  id="api_identifier"
                  name="api_identifier"
                  className={styles.textboxes}
                />
                <label
                  htmlFor="logo"
                  className={`${styles.labels} ${styles.logo_label}`}
                >
                  Logo
                </label>
                <img
                  // src={logo ? URL.createObjectURL(logo) : null}
                  src={logo ? URL.createObjectURL(logo) : logoUrl}
                  alt="Logo preview"
                  className={styles.logo}
                  style={
                    logoUrl || logo ? { display: "block" } : { display: "none" }
                  }
                />
                <input
                  type="file"
                  id="logo"
                  name="logo"
                  accept=".png, .webp, .svg"
                  className={styles.textboxes}
                  onChange={(e) => handleLogoChange(e)}
                />
                <div className={styles.active}>
                  <input
                    type="checkbox"
                    value={active}
                    checked={active}
                    onChange={() => setActive(!active)}
                    id="active"
                    name="active"
                    className={styles.checkbox}
                  />
                  <label
                    htmlFor="active"
                    className={`${styles.labels} ${styles.checkbox_label}`}
                  >
                    Active
                  </label>
                </div>
                <div className={styles.fn_btns}>
                  <button
                    onClick={() => navigate("/hotels")}
                    title="Return to client list"
                    className="neutral_btn"
                  >
                    Cancel
                  </button>
                  <button type="submit" title="Update this hotel">
                    Update hotel
                  </button>
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </>
  );
}

export default EditHotel;
